import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/lib/adminAuth";
import {
  Users, Building2, LayoutGrid, HeartPulse, Activity, ClipboardList,
  Receipt, CreditCard, Wallet, CheckCircle2, PackageOpen, LogOut, Bell, ShieldCheck, ChevronRight, Sparkles, FileText,
} from "lucide-react";

const NAV = [
  { to: "/back-office", label: "Dashboard", icon: LayoutGrid, end: true },
  { to: "/back-office/users", label: "User Management", icon: Users },
  { to: "/back-office/patients", label: "Patient Management", icon: HeartPulse },
  { to: "/back-office/nudges", label: "Nudge Centre", icon: Sparkles },
  { to: "/back-office/prd", label: "Product Brief (PRD)", icon: FileText },
  { to: "/back-office/organizations", label: "Organization", icon: Building2, disabled: true },
  { to: "/back-office/masters", label: "Master Management", icon: LayoutGrid, disabled: true },
  { to: "/back-office/activity", label: "Activity Template", icon: Activity, disabled: true },
  { to: "/back-office/plans-template", label: "Plan Template", icon: ClipboardList, disabled: true },
  { to: "/back-office/transactions", label: "Transaction", icon: Receipt, disabled: true },
  { to: "/back-office/payment", label: "Payment Setup", icon: CreditCard, disabled: true },
  { to: "/back-office/settlement", label: "Settlement", icon: Wallet, disabled: true },
  { to: "/back-office/plan-status", label: "Plan Status", icon: CheckCircle2, disabled: true },
  { to: "/back-office/plans", label: "Plans Management", icon: PackageOpen, disabled: true },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F7F4EC] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-[#EFECE5] flex-none flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-6 border-b border-[#EFECE5]">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center">
              <ShieldCheck size={18} />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-[#0A4C36] leading-tight" style={{ fontFamily: "Instrument Sans" }}>
                HealthCircles
              </div>
              <div className="text-[10px] text-[#8A9992] uppercase tracking-widest">Back Office · v1.0.0</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4">
          {NAV.map((n) => {
            if (n.disabled) {
              return (
                <div key={n.to} className="mx-3 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] text-[#B5C0BA] cursor-not-allowed" title="Coming soon">
                  <n.icon size={16} strokeWidth={1.75} /> {n.label}
                  <span className="ml-auto text-[9px] uppercase tracking-widest">Soon</span>
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `mx-3 my-0.5 flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] transition-colors ${
                    isActive
                      ? "bg-[#0A4C36] text-white font-semibold"
                      : "text-[#47554D] hover:bg-[#E6F0EC] hover:text-[#0A4C36]"
                  }`
                }
              >
                <n.icon size={16} strokeWidth={1.75} /> {n.label}
                <ChevronRight size={12} className="ml-auto opacity-40" />
              </NavLink>
            );
          })}
        </nav>
        <div className="border-t border-[#EFECE5] p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#FCE7D9] text-[#5C3A21] flex items-center justify-center text-sm font-semibold">A</div>
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-semibold text-[#1F2A24] truncate">{admin?.name || "Admin"}</div>
              <div className="text-[10px] text-[#8A9992] truncate">{admin?.email}</div>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="w-8 h-8 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#8A9992] hover:text-[#B14A2A]"
              title="Sign out"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-[#EFECE5] px-8 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#8A9992]">Restricted operator area</div>
            <div className="text-lg font-semibold text-[#1F2A24]" style={{ fontFamily: "Instrument Sans" }}>HealthCircles Back Office</div>
          </div>
          <div className="flex items-center gap-3">
            <button className="w-9 h-9 rounded-full hover:bg-[#F3F1EC] flex items-center justify-center text-[#8A9992]"><Bell size={16} /></button>
            <button onClick={() => nav("/back-office")} className="btn-outline-emerald text-xs px-3 py-1.5">Home</button>
          </div>
        </header>
        <div className="p-8 flex-1"><Outlet /></div>
      </main>
    </div>
  );
}
